import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '../context/WalletProvider';
import { useToast, readableError } from '../components/Toast';
import Loader from '../components/Loader';
import {
  CountUpUSDT, CountUpInt, LevelProgress, TeamStrength, DashboardSkeleton,
} from '../components/Widgets';
import { fetchUser, fetchLevels, claim, fetchWithdrawFee } from '../services/zen';
import { getProfile, getNames, getWeek } from '../services/api';
import { fmtDate, money } from '../utils/format';
import { ethers } from 'ethers';

export default function Dashboard() {
  const { provider, signer, address, isCorrectNetwork, ensureNetwork } = useWallet();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [txBusy, setTxBusy] = useState(false);
  const [user, setUser] = useState(null);
  const [levels, setLevels] = useState(null);
  const [myName, setMyName] = useState('');
  const [uplineName, setUplineName] = useState('');
  const [week, setWeek] = useState(null); // { income: BN, profit: BN } this week
  const [feeBps, setFeeBps] = useState(0);

  const load = async () => {
    if (!provider || !address || !isCorrectNetwork) { setLoading(false); return; }
    try {
      setLoading(true);
      const u = await fetchUser(provider, address);
      setUser(u);
      if (u.registered) {
        const lv = await fetchLevels(provider, u.id);
        setLevels(lv);
        try { const f = await fetchWithdrawFee(provider); setFeeBps(f); } catch (_) {}
        try { const p = await getProfile(address); if (p) setMyName(p.name || ''); } catch (_) {}
        try {
          if (u.referrerId && u.referrerId !== '0') {
            const nm = await getNames([u.referrerId]);
            setUplineName(nm[u.referrerId]?.name || '');
          }
        } catch (_) {}
      }
    } catch (_) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => { if (alive) await load(); })();
    return () => { alive = false; };
  }, [provider, address, isCorrectNetwork]);

  // this-week = the latest distribution's profit + income for this user (from DB)
  useEffect(() => {
    let alive = true;
    if (!user || !user.registered) return;
    (async () => {
      try {
        const w = await getWeek(user.id);
        if (!alive) return;
        setWeek({
          income: ethers.BigNumber.from(w.income || '0'),
          profit: ethers.BigNumber.from(w.profit || '0'),
        });
      } catch (_) {
        if (alive) setWeek({ income: ethers.BigNumber.from(0), profit: ethers.BigNumber.from(0) });
      }
    })();
    return () => { alive = false; };
  }, [user]);

  const onClaim = async () => {
    if (!(await ensureNetwork())) { toast.error('Please switch to the correct network'); return; }
    try {
      setTxBusy(true);
      await claim(signer);
      toast.success('Rewards claimed to your wallet');
      await load();
    } catch (e) {
      toast.error(readableError(e));
    } finally {
      setTxBusy(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h1 className="page-title">Dashboard</h1>
        <DashboardSkeleton />
      </div>
    );
  }

  if (!user || !user.registered) {
    return (
      <div>
        <h1 className="page-title">Dashboard</h1>
        <div className="card center rise d1">
          <p className="muted" style={{ marginBottom: 16 }}>
            Your wallet isn't registered yet. Join with a referral link to start earning level income.
          </p>
          <Link to="/account"><button className="btn">Register Now</button></Link>
        </div>
      </div>
    );
  }

  const refLink = `${window.location.origin}/account?ref=${user.accountNumber}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(refLink); toast.success('Referral link copied'); }
    catch (_) { toast.error('Could not copy link'); }
  };

  const hasRewards = user.pendingRewards.gt(0);
  const totalTeam = levels ? levels.memberCounts.reduce((a, b) => a + b, 0) : 0;

  return (
    <div>
      {txBusy && <Loader messages={['Confirm in your wallet', 'Sending USDT to you', 'Almost there']} />}
      <h1 className="page-title">Dashboard</h1>

      {user.blacklisted && (
        <div className="banner bad">This account is currently suspended. Contact support.</div>
      )}

      {/* MEMBER IDENTITY CARD */}
      <div className="member-card rise d1">
        <span className="metal" />
        <div className="mc-top">
          <span className="mc-chip">Zen Member</span>
          <span className="mc-chip id-chip">ID {user.id}</span>
        </div>
        <div className="mc-name">{myName || 'Zen Investor'}</div>
        <div className="mc-acct">MT5 · {user.accountNumber}</div>
        <div className="mc-divider" />
        <div className="mc-grid">
          <div className="mc-cell"><div className="l">Upline ID</div><div className="v up">{user.referrerId}</div></div>
          <div className="mc-cell"><div className="l">Upline Name</div><div className="v">{uplineName || '—'}</div></div>
          <div className="mc-cell"><div className="l">Direct Referrals</div><div className="v id">{user.directReferrals}</div></div>
          <div className="mc-cell"><div className="l">Joined</div><div className="v">{fmtDate(user.registrationTime)}</div></div>
        </div>
      </div>

      <div className="spacer-18" />

      {/* REFERRAL LINK */}
      <div className="card rise d2">
        <div className="card-title">Your Referral Link</div>
        <div className="mono" style={{ fontSize: 12, color: 'var(--blue-bright)', wordBreak: 'break-all', marginBottom: 12 }}>{refLink}</div>
        <button className="btn gold" onClick={copyLink}>Copy Referral Link</button>
      </div>

      <div className="spacer-18" />

      {/* CLAIM HERO */}
      <div className={'balance-card center claim-hero rise d3' + (hasRewards ? ' has-rewards' : '')}>
        <div className="balance-label">Claimable Rewards</div>
        <div className="balance-amount" style={{ fontSize: 46 }}>
          <CountUpUSDT value={user.pendingRewards} /><span className="unit">USDT</span>
        </div>
      </div>

      <div className="spacer-18" />

      <button className="claim-big rise d3" onClick={onClaim} disabled={txBusy || !hasRewards || user.blacklisted}>
        {txBusy ? <span className="btn-spin" /> : hasRewards ? 'CLAIM REWARDS' : 'NOTHING TO CLAIM'}
      </button>
      {feeBps > 0 && hasRewards && (
        <p className="muted center" style={{ fontSize: 11.5, marginTop: 8 }}>
          A {feeBps / 100}% withdrawal fee applies. You'll receive {money(user.pendingRewards.mul(10000 - feeBps).div(10000))} USDT.
        </p>
      )}

      <div className="spacer-18" />

      {/* EARNINGS SUMMARY */}
      <div className="stat-grid rise d4">
        <div className="stat"><div className="k">Total Earned</div><div className="v gold"><CountUpUSDT value={user.totalEarned} /></div></div>
        <div className="stat"><div className="k">Total Profit Reported</div><div className="v"><CountUpUSDT value={user.totalProfitReported} /></div></div>
      </div>

      <div className="spacer-12" />

      {/* THIS WEEK */}
      <div className="stat-grid rise d4">
        <div className="stat" style={{ borderColor: 'var(--gold-glow)' }}>
          <div className="k">This Week Income</div>
          <div className="v gold">
            {week === null ? <span className="skel skel-line" style={{ width: 70, display: 'inline-block', height: 18 }} /> : <CountUpUSDT value={week.income} />}
          </div>
        </div>
        <div className="stat" style={{ borderColor: 'var(--border-strong)' }}>
          <div className="k">This Week Profit</div>
          <div className="v up">
            {week === null ? <span className="skel skel-line" style={{ width: 70, display: 'inline-block', height: 18 }} /> : <CountUpUSDT value={week.profit} />}
          </div>
        </div>
      </div>

      <div className="spacer-18" />

      {/* LEVEL PROGRESS */}
      <div className="card rise d5">
        <div className="card-title">Level Unlock Progress</div>
        <LevelProgress levels={levels} directReferrals={user.directReferrals} />
      </div>

      <div className="spacer-18" />

      {/* TEAM STRENGTH */}
      <div className="card rise d6">
        <div className="row-between" style={{ marginBottom: 12 }}>
          <span className="card-title" style={{ margin: 0 }}>Team Strength</span>
          <span className="mono" style={{ color: 'var(--gold-bright)', fontSize: 13 }}>
            <CountUpInt value={totalTeam} /> total
          </span>
        </div>
        <TeamStrength memberCounts={levels ? levels.memberCounts : null} />
      </div>

      <div className="spacer-18" />
    </div>
  );
}
